import React, { useState } from "react";
import { orderService } from "../services/apiService";
import Toast from "../components/Toast";

const ReturnModal = ({ order, onClose, onSuccess }) => {
  const [items, setItems] = useState(
    (order.items || []).map((item) => ({
      ...item,
      returnQty: 0,
      maxReturn: (item.quantity || 0) - (item.returnedQuantity || 0),
    }))
  );
  const [reason, setReason] = useState("");
  const [refundMode, setRefundMode] = useState(order.paymentMode || "CASH");
  const [transactionId, setTransactionId] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const selectedItems = items.filter((i) => i.returnQty > 0);
  const totalRefund = selectedItems.reduce((sum, i) => {
    const unitPrice = i.sellingPrice / i.quantity;
    return sum + unitPrice * i.returnQty;
  }, 0);

  const handleQtyChange = (idx, val) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === idx ? { ...item, returnQty: Math.min(Math.max(0, parseInt(val) || 0), item.maxReturn) } : item
      )
    );
  };

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      setToast({ type: "error", message: "Select at least one item to return" });
      return;
    }
    if (!reason.trim()) {
      setToast({ type: "error", message: "Return reason is required" });
      return;
    }
    if (refundMode !== "CASH" && !transactionId.trim()) {
      setToast({ type: "error", message: "Transaction ID is required for non-cash refund" });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        orderId: order.orderId,
        reason: reason.trim(),
        refundMode,
        transactionId: refundMode === "CASH" ? "CASH_REFUND" : transactionId.trim(),
        items: selectedItems.map((i) => ({
          orderItemId: i.orderItemId,
          returnQuantity: i.returnQty,
          hsnCode: i.hsnCode || null,
          gstRate: i.gstRate || 0,
          productName: i.productName || null,
        })),
      };
      const res = await orderService.processReturn(payload);
      onSuccess(res);
    } catch (err) {
      const msg = err.response?.data?.errorMessage || err.response?.data?.message || err.response?.data?.error || "Return failed";
      setToast({ type: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="fas fa-undo-alt text-white text-lg" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm sm:text-base">Return / Refund</h3>
              <p className="text-white/80 text-xs">Order #{order.orderId} • {order.billNo || "-"}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition">
            <i className="fas fa-times text-lg" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Select items to return</p>
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={idx} className={`border rounded-xl p-3 transition ${item.returnQty > 0 ? "border-red-300 bg-red-50" : "border-gray-200"}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-800 truncate">{item.productName}</p>
                    <p className="text-xs text-gray-400">
                      Qty: {item.quantity} • Returned: {item.returnedQuantity || 0} • Available: {item.maxReturn}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Return:</span>
                    <input
                      type="number"
                      min="0"
                      max={item.maxReturn}
                      value={item.returnQty || ""}
                      placeholder="0"
                      disabled={item.maxReturn <= 0}
                      onChange={(e) => handleQtyChange(idx, e.target.value)}
                      className={`w-14 text-center text-xs border rounded-lg py-1 px-1 focus:outline-none focus:ring-2 focus:ring-red-400 ${
                        item.maxReturn <= 0 ? "bg-gray-100 cursor-not-allowed" : "bg-white border-gray-300"
                      }`}
                    />
                  </div>
                </div>
                {item.returnQty > 0 && (
                  <p className="text-xs text-red-600 mt-1 font-medium">
                    Refund: ₹{((item.sellingPrice / item.quantity) * item.returnQty).toFixed(2)}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Reason & Refund Mode */}
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-500">Reason *</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Damaged, Wrong item, Customer request"
                className="w-full mt-1 text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
              />
            </div>
            <div className="flex gap-3 flex-wrap">
              <div className="flex-1 min-w-[120px]">
                <label className="text-xs font-semibold text-gray-500">Refund Mode</label>
                <select
                  value={refundMode}
                  onChange={(e) => setRefundMode(e.target.value)}
                  className="w-full mt-1 text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
                >
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="CARD">Card</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                </select>
              </div>
              {refundMode !== "CASH" && (
                <div className="flex-1 min-w-[120px]">
                  <label className="text-xs font-semibold text-gray-500">Transaction ID *</label>
                  <input
                    type="text"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    placeholder="Refund Txn ID"
                    className="w-full mt-1 text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-5 py-3 bg-gray-50 flex items-center justify-between flex-shrink-0 flex-wrap gap-2">
          <div>
            <p className="text-xs text-gray-500">Total Refund</p>
            <p className="text-lg font-bold text-red-600">₹{totalRefund.toFixed(2)}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 text-xs font-medium transition">
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={loading || selectedItems.length === 0}
              className={`px-5 py-2 rounded-lg text-xs font-semibold text-white shadow transition ${
                loading || selectedItems.length === 0
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
              }`}>
              {loading ? <i className="fas fa-spinner fa-spin mr-1" /> : <i className="fas fa-undo-alt mr-1" />}
              Process Return
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnModal;
